import { useEffect } from 'react'
import { DayOfWeek, DayHours, TimePeriod } from '@/types/venue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Trash2 } from 'lucide-react'

interface HoursEditorProps {
  hours: DayHours[]
  onChange: (hours: DayHours[]) => void
}

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
]

// Helper to create default hours for a day
const createDefaultDayHours = (day: DayOfWeek): DayHours => ({
  day,
  isClosed: false,
  periods: [{ open: '09:00', close: '17:00' }],
})

// Ensure all 7 days are present in the hours array
const ensureAllDays = (hours: DayHours[]): DayHours[] => {
  const existingDays = new Set(hours.map(h => h.day))
  const result = [...hours]

  for (const day of DAYS) {
    if (!existingDays.has(day.value)) {
      result.push(createDefaultDayHours(day.value))
    }
  }

  // Sort by day order
  return result.sort((a, b) => {
    const aIndex = DAYS.findIndex(d => d.value === a.day)
    const bIndex = DAYS.findIndex(d => d.value === b.day)
    return aIndex - bIndex
  })
}

export function HoursEditor({ hours, onChange }: HoursEditorProps) {
  // Initialize all 7 days on mount and when hours prop changes
  useEffect(() => {
    // Check if any days are missing
    const existingDays = new Set(hours.map(h => h.day))
    const missingDays = DAYS.filter(d => !existingDays.has(d.value))

    if (missingDays.length > 0) {
      // Add missing days with defaults
      const completeHours = ensureAllDays(hours)
      onChange(completeHours)
    }
  }, []) // Only run on mount - don't re-run when hours changes to avoid infinite loop

  const getDayHours = (day: DayOfWeek): DayHours => {
    return hours.find((h) => h.day === day) || createDefaultDayHours(day)
  }

  const updateDayHours = (day: DayOfWeek, updates: Partial<DayHours>) => {
    const newHours = hours.filter((h) => h.day !== day)
    const dayHours = getDayHours(day)
    newHours.push({ ...dayHours, ...updates })
    onChange(newHours)
  }

  const toggleClosed = (day: DayOfWeek) => {
    const dayHours = getDayHours(day)
    updateDayHours(day, { isClosed: !dayHours.isClosed })
  }

  const addPeriod = (day: DayOfWeek) => {
    const dayHours = getDayHours(day)
    const newPeriods = [...dayHours.periods, { open: '09:00', close: '17:00' }]
    updateDayHours(day, { periods: newPeriods })
  }

  const removePeriod = (day: DayOfWeek, index: number) => {
    const dayHours = getDayHours(day)
    const newPeriods = dayHours.periods.filter((_, i) => i !== index)
    updateDayHours(day, { periods: newPeriods })
  }

  const updatePeriod = (day: DayOfWeek, index: number, field: 'open' | 'close', value: string) => {
    const dayHours = getDayHours(day)
    const newPeriods = dayHours.periods.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    )
    updateDayHours(day, { periods: newPeriods })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Hours of Operation</h3>
      </div>

      {DAYS.map((day) => {
        const dayHours = getDayHours(day.value)
        return (
          <div key={day.value} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="font-medium w-28">{day.label}</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dayHours.isClosed}
                    onChange={() => toggleClosed(day.value)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-600">Closed</span>
                </label>
              </div>
            </div>

            {!dayHours.isClosed && (
              <div className="space-y-2 mt-3">
                {dayHours.periods.map((period, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={period.open}
                      onChange={(e) => updatePeriod(day.value, index, 'open', e.target.value)}
                      className="w-32"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="time"
                      value={period.close}
                      onChange={(e) => updatePeriod(day.value, index, 'close', e.target.value)}
                      className="w-32"
                    />
                    {dayHours.periods.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePeriod(day.value, index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPeriod(day.value)}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Hours
                </Button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
